import React, { FC, useCallback, useEffect, useState } from "react";
import { Button, TButtonProps } from "kanopi-components/Button";
import { LabelLarge } from "baseui/typography";
import { LabelSmall } from "kanopi-components/typography";
import { UploadedFile } from "kanopi-components/FileUploader/UploadedFile";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import {
  FileUploader as BWFileUploader,
  FileUploaderProps as BWFileUploaderProps,
} from "baseui/file-uploader";

export type TFileWithPath = File & {
  key?: string;
  name?: string;
  size?: number;
  date?: string;
  link?: string;
};
type TFileUploaderProps = {
  buttonOverrides?: TButtonProps;
  file?: TFileWithPath;
  description?: string;
  displayMode?: boolean;
  uploading?: boolean;
  uploadingText?: string;
  uploadError?: boolean;
  onFileSelect?: (file: TFileWithPath[]) => void;
  onFileDelete?: () => void;
  onUploadRetry?: () => void;
};
export enum FileTypes {
  PDF = ".pdf",
  XLS = ".xls",
  CSV = ".csv",
  JPG = ".jpg",
  PNG = ".png",
  DOC = ".doc,.docx",
}
export const FileUploader: FC<TFileUploaderProps & BWFileUploaderProps> = ({
  buttonOverrides = {},
  file = null,
  description,
  displayMode = false,
  uploading = false,
  uploadError = false,
  uploadingText = "Uploading...",
  onFileSelect = () => undefined,
  onUploadRetry = () => undefined,
  onFileDelete = () => undefined,
  multiple = false,
  disabled,
  ...bwFileUploaderProps
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: File[] = []) => {
      if (!multiple && rejectedFiles.length > 1) {
        throw new Error("Multiple file selections are not allowed");
      } else if (rejectedFiles.length > 0) {
        throw new Error("Selected file formats are not accepted.");
      } else {
        onFileSelect(acceptedFiles);
      }
    },
    [multiple, onFileSelect]
  );
  const [, theme] = useStyletron();
  const [buttonText, setButtonText] = useState("Select file");
  const [uploadStatusLabel, setUploadStatusLabel] = useState<string>();
  const [isReadOnly, setIsReadOnly] = useState<boolean>();
  const [isFileSelected, setIsFileSelected] = useState<boolean>();
  useEffect(() => {
    if (file?.size > 0) {
      setButtonText("Select another file");
    }
    setIsFileSelected(file?.size > 0);
  }, [file?.size]);
  useEffect(() => {
    const _label =
      (uploading && uploadingText) ||
      (uploadError && "Error uploading file") ||
      undefined;
    setUploadStatusLabel(_label);
  }, [uploading, uploadError, uploadingText]);

  useEffect(() => {
    setIsReadOnly(disabled || displayMode || file?.size > 0);
  }, [disabled, displayMode, file?.size]);
  return (
    <BWFileUploader
      {...bwFileUploaderProps}
      multiple={multiple}
      onDrop={onDrop}
      disabled={isReadOnly || uploading}
      overrides={{
        Root: {
          style: () => ({
            width: "100%",
          }),
        },
        FileDragAndDrop: {
          style: ({ $theme }) => ({
            borderTopWidth: "1px",
            borderBottomWidth: "1px",
            borderLeftWidth: "1px",
            borderRightWidth: "1px",
            borderTopColor: `${$theme.colors.black}`,
            borderBottomColor: `${$theme.colors.black}`,
            borderLeftColor: `${$theme.colors.black}`,
            borderRightColor: `${$theme.colors.black}`,
            backgroundColor: "none",
          }),
        },
        ContentMessage: {
          component: () => {
            return (
              <>
                {uploading || uploadError ? (
                  <>
                    <LabelSmall>{uploadStatusLabel}</LabelSmall>
                  </>
                ) : (
                  <>
                    {isFileSelected ? (
                      <UploadedFile
                        file={file}
                        displayMode={displayMode}
                        description={description}
                        deleteFile={onFileDelete}
                      />
                    ) : (
                      <LabelLarge color={theme.colors.contentInverseSecondary}>
                        Add a file
                      </LabelLarge>
                    )}
                  </>
                )}
              </>
            );
          },
        },
        ButtonComponent: {
          component: (props) => {
            return (
              <>
                {!isReadOnly && (
                  <>
                    {uploadError ? (
                      <Button
                        size={Button.SIZE.mini}
                        kind={Button.KIND.tertiary}
                        overrides={{
                          BaseButton: {
                            style: {
                              marginTop: theme.sizing.scale500,
                            },
                          },
                        }}
                        onClick={onUploadRetry}
                      >
                        Retry
                      </Button>
                    ) : (
                      <Button {...props} {...buttonOverrides} isLoading={uploading}>
                        {buttonText.toLocaleUpperCase()}
                      </Button>
                    )}
                  </>
                )}
              </>
            );
          },
        },
      }}
    />
  );
};
